import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Select,
  Checkbox,
  Textarea,
  useToast,
  Box,
  Text,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";

const LANGUAGES = [
  { value: "한국어", label: "한국어" },
  { value: "영어", label: "영어" },
  { value: "기타", label: "기타" },
];

const GENRES = [
  { value: "트로트", label: "트로트" },
  { value: "Ballad (국내)", label: "발라드 (국내)" },
  { value: "Blues (국내)", label: "블루스 (국내)" },
  { value: "Dance (국내)", label: "댄스 (국내)" },
  { value: "Folk (국내)", label: "포크 (국내)" },
  { value: "R&B / Soul (국내)", label: "알앤비 / 소울 (국내)" },
  { value: "Rap / Hip-hop (국내)", label: "랩 / 힙합 (국내)" },
  { value: "Rock (국내)", label: "록 (국내)" },
  { value: "Electronica (국내)", label: "일렉트로니카 (국내)" },
  { value: "CCM (국내)", label: "CCM (국내)" },
  { value: "Jazz (국내)", label: "재즈 (국내)" },
  { value: "OST (국내)", label: "OST (국내)" },
  { value: "Blues (국외)", label: "블루스 (국외)" },
  { value: "Country (국외)", label: "컨트리 (국외)" },
  { value: "Electronica (국외)", label: "일렉트로니카 (국외)" },
  { value: "Folk (국외)", label: "포크 (국외)" },
  { value: "Pop (국외)", label: "팝 (국외)" },
  { value: "EDM (국외)", label: "EDM (국외)" },
  { value: "R&B / Soul (국외)", label: "알앤비 / 소울 (국외)" },
  { value: "Rap / Hip-hop (국외)", label: "랩 / 힙합 (국외)" },
  { value: "Rock (국외)", label: "록 (국외)" },
  { value: "CCM (국외)", label: "CCM (국외)" },
  { value: "Jazz (국외)", label: "재즈 (국외)" },
  { value: "OST (국외)", label: "OST (국외)" },
  { value: "한국전통음악", label: "한국전통음악" },
  { value: "어린이음악", label: "어린이음악" },
  { value: "종교음악", label: "종교음악" },
  { value: "Classic", label: "클래식" },
  { value: "New Age", label: "뉴에이지" },
  { value: "뮤직테라피", label: "뮤직테라피" },
  { value: "기타", label: "기타" },
  { value: "월드뮤직", label: "월드뮤직" },
  { value: "Electronica (J-Pop)", label: "일렉트로니카 (J-Pop)" },
  { value: "Pop (J-Pop)", label: "팝 (J-Pop)" },
  { value: "R&B / Soul (J-Pop)", label: "알앤비 / 소울 (J-Pop)" },
  { value: "Rap / Hip-hop (J-Pop)", label: "랩 / 힙합 (J-Pop)" },
  { value: "Rock (J-Pop)", label: "록 (J-Pop)" },
];

const MOODS = [
  { value: "Angry", label: "화남" },
  { value: "Busy & Frantic", label: "바쁘고 정신없음" },
  { value: "Changing Tempo", label: "변하는 템포" },
  { value: "Chasing", label: "쫓는 느낌" },
  { value: "Dark", label: "어두움" },
  { value: "Dreamy", label: "몽환적임" },
  { value: "Eccentric", label: "엉뚱함" },
  { value: "Elegant", label: "우아함" },
  { value: "Epic", label: "웅장함" },
  { value: "Euphoric", label: "황홀함" },
  { value: "Fear", label: "두려움" },
  { value: "Floating", label: "둥둥 떠다님" },
  { value: "Funny", label: "유쾌함" },
  { value: "Glamorous", label: "매혹적임" },
  { value: "Happy", label: "행복함" },
  { value: "Heavy & Ponderous", label: "무겁고 육중함" },
  { value: "Hopeful", label: "희망적임" },
  { value: "Laid Back", label: "느긋함" },
  { value: "Marching", label: "행진하는 느낌" },
  { value: "Mysterious", label: "신비로움" },
  { value: "Peaceful", label: "평화로움" },
  { value: "Quirky", label: "기이함" },
  { value: "Relaxing", label: "편안함" },
  { value: "Restless", label: "불안정함" },
  { value: "Romantic", label: "로맨틱함" },
  { value: "Running", label: "달림" },
  { value: "Sad", label: "슬픔" },
  { value: "Scary", label: "무서움" },
  { value: "Sentimental", label: "감상적임" },
  { value: "Sexy", label: "섹시함" },
  { value: "Smooth", label: "부드러움" },
  { value: "Sneaking", label: "살금살금 움직임" },
  { value: "Suspense", label: "서스펜스" },
  { value: "Weird", label: "이상함" },
  { value: "All moods", label: "전체 무드" },
];

const TrackEditModal = ({
  isOpen,
  onClose,
  track,
  albumData,
  tracks,
  onSuccess,
}) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [musicians, setMusicians] = useState([]);
  const [formData, setFormData] = useState({ ...track });

  useEffect(() => {
    if (isOpen && track) {
      setFormData({
        ...track,
        mood: Array.isArray(track.mood)
          ? track.mood
          : typeof track.mood === "string" && track.mood.startsWith("[")
          ? JSON.parse(track.mood)
          : [],
      });
    }
  }, [isOpen, track]);

  useEffect(() => {
    const fetchMusicians = async () => {
      try {
        const { data, error } = await supabase
          .from("musician")
          .select("*")
          .eq("user", albumData.user)
          .order("created_date", { ascending: false });
        if (error) throw error;
        setMusicians(data || []);
      } catch (error) {
        toast({
          title: "뮤지션 목록을 불러오는데 실패했습니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };
    if (isOpen) fetchMusicians();
  }, [isOpen, albumData.user]);

  // 분위기 다중 선택: 이미 선택된 항목은 해제, 새로운 항목은 기존 선택에 추가(토글)
  const handleMoodToggle = (mood) => {
    setFormData((prev) => {
      const moodArr = Array.isArray(prev.mood) ? prev.mood : [];
      if (moodArr.includes(mood.value)) {
        // 이미 선택된 경우 해제
        return { ...prev, mood: moodArr.filter((m) => m !== mood.value) };
      } else {
        // 기존 선택 + 새 항목 추가
        return { ...prev, mood: [...moodArr, mood.value] };
      }
    });
  };

  // 트랙 번호 변경 가능 조건: 자기 자신 or 없는 번호만
  const usedTrackNumbers = tracks
    .filter((t) => t.id !== track.id)
    .map((t) => String(t.track_number));

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // 필수값 검증
      const requiredFields = {
        track_number: "트랙 번호",
        track_name_kr: "트랙명",
        musician: "뮤지션",
        vocal_language: "언어",
        genre_depth4: "장르",
        composer_kr: "작곡가",
        composer_en: "작곡가(영문)",
      };
      const missingFields = Object.entries(requiredFields)
        .filter(([key]) => !formData[key])
        .map(([_, label]) => label);
      if (missingFields.length > 0) {
        toast({
          title: "필수 정보 누락",
          description: `${missingFields.join(", ")}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
        return;
      }
      // 트랙 번호 중복 체크
      if (
        formData.track_number &&
        usedTrackNumbers.includes(String(formData.track_number))
      ) {
        toast({
          title: "트랙 번호 중복",
          description: "이미 존재하는 트랙 번호입니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
        return;
      }
      // 업데이트
      const { error } = await supabase
        .from("track")
        .update({
          ...formData,
          track_number: parseInt(formData.track_number),
        })
        .eq("id", track.id);
      if (error) throw error;
      toast({
        title: "트랙 정보가 수정되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "트랙 수정 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!track || !tracks || !track.id) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent maxW="800px" borderRadius="xl" boxShadow="xl">
        <ModalHeader
          bg="blue.50"
          borderTopRadius="xl"
          py={4}
          fontSize="xl"
          fontWeight="bold"
          color="blue.700"
        >
          트랙 정보 수정
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={6}>
          <VStack spacing={4}>
            <HStack width="100%" spacing={4}>
              <FormControl flex={1} isRequired>
                <FormLabel fontWeight="medium" color="gray.700">
                  트랙 번호
                </FormLabel>
                <Input
                  name="track_number"
                  value={formData.track_number}
                  onChange={(e) =>
                    setFormData({ ...formData, track_number: e.target.value })
                  }
                  type="number"
                  borderRadius="md"
                  focusBorderColor="blue.400"
                  min={1}
                />
                <Text fontSize="xs" color="gray.500">
                  이미 존재하는 트랙 번호로는 변경 불가
                </Text>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontWeight="medium" color="gray.700">
                  타이틀 곡
                </FormLabel>
                <Checkbox
                  name="track_main"
                  isChecked={
                    formData.track_main === true ||
                    formData.track_main === "true"
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, track_main: e.target.checked })
                  }
                  colorScheme="blue"
                >
                  타이틀 곡으로 지정
                </Checkbox>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontWeight="medium" color="gray.700">
                  가창/연주
                </FormLabel>
                <Checkbox
                  name="vocal"
                  isChecked={
                    formData.vocal === true || formData.vocal === "true"
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, vocal: e.target.checked })
                  }
                  colorScheme="blue"
                >
                  가창곡
                </Checkbox>
              </FormControl>
            </HStack>
            <Box width="100%">
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="gray.700">
                  트랙명
                </FormLabel>
              </FormControl>
              <Text fontSize="sm" color="gray.500" mb={2}>
                국문에도 영어가 가능합니다. 국문 트랙명은 한국 발매 이름이고,
                영문 트랙명은 해외 발매 이름이 됩니다.
              </Text>
              <HStack width="100%" spacing={4}>
                <FormControl flex={1} isRequired>
                  <Input
                    name="track_name_kr"
                    value={formData.track_name_kr}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        track_name_kr: e.target.value,
                      })
                    }
                    placeholder="국문 트랙명"
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
                <FormControl flex={1} isRequired>
                  <Input
                    name="track_name_en"
                    value={formData.track_name_en}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        track_name_en: e.target.value,
                      })
                    }
                    placeholder="영문 트랙명"
                    borderRadius="md"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </HStack>
            </Box>
            <HStack width="100%" spacing={4}>
              <FormControl flex={1} isRequired>
                <FormLabel fontWeight="medium" color="gray.700">
                  뮤지션
                </FormLabel>
                <Select
                  name="musician"
                  value={formData.musician}
                  onChange={(e) =>
                    setFormData({ ...formData, musician: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                >
                  <option value="">선택하세요</option>
                  {musicians.map((musician) => (
                    <option key={musician.id} value={musician.id}>
                      {musician.name_musician}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl flex={1} isRequired>
                <FormLabel fontWeight="medium" color="gray.700">
                  언어
                </FormLabel>
                <Select
                  name="vocal_language"
                  value={formData.vocal_language}
                  onChange={(e) =>
                    setFormData({ ...formData, vocal_language: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                >
                  <option value="">선택하세요</option>
                  {LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl flex={1} isRequired>
                <FormLabel fontWeight="medium" color="gray.700">
                  장르
                </FormLabel>
                <Select
                  name="genre_depth4"
                  value={formData.genre_depth4}
                  onChange={(e) =>
                    setFormData({ ...formData, genre_depth4: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                >
                  <option value="">선택하세요</option>
                  {GENRES.map((genre) => (
                    <option key={genre.value} value={genre.value}>
                      {genre.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>
            <HStack width="100%" spacing={4}>
              <FormControl flex={1}>
                <FormLabel fontWeight="medium" color="gray.700">
                  작사가
                </FormLabel>
                <Input
                  name="lyricist_kr"
                  value={formData.lyricist_kr}
                  onChange={(e) =>
                    setFormData({ ...formData, lyricist_kr: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontWeight="medium" color="gray.700">
                  작사가 (영문)
                </FormLabel>
                <Input
                  name="lyricist_en"
                  value={formData.lyricist_en}
                  onChange={(e) =>
                    setFormData({ ...formData, lyricist_en: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>
            </HStack>
            <HStack width="100%" spacing={4}>
              <FormControl flex={1} isRequired>
                <FormLabel fontWeight="medium" color="gray.700">
                  작곡가
                </FormLabel>
                <Input
                  name="composer_kr"
                  value={formData.composer_kr}
                  onChange={(e) =>
                    setFormData({ ...formData, composer_kr: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>
              <FormControl flex={1} isRequired>
                <FormLabel fontWeight="medium" color="gray.700">
                  작곡가 (영문)
                </FormLabel>
                <Input
                  name="composer_en"
                  value={formData.composer_en}
                  onChange={(e) =>
                    setFormData({ ...formData, composer_en: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>
            </HStack>
            <HStack width="100%" spacing={4}>
              <FormControl flex={1}>
                <FormLabel fontWeight="medium" color="gray.700">
                  편곡가
                </FormLabel>
                <Input
                  name="arranger_kr"
                  value={formData.arranger_kr}
                  onChange={(e) =>
                    setFormData({ ...formData, arranger_kr: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontWeight="medium" color="gray.700">
                  편곡가 (영문)
                </FormLabel>
                <Input
                  name="arranger_en"
                  value={formData.arranger_en}
                  onChange={(e) =>
                    setFormData({ ...formData, arranger_en: e.target.value })
                  }
                  borderRadius="md"
                  focusBorderColor="blue.400"
                />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontWeight="medium" color="gray.700">
                가사
              </FormLabel>
              <Textarea
                name="lyrics"
                value={formData.lyrics}
                onChange={(e) =>
                  setFormData({ ...formData, lyrics: e.target.value })
                }
                rows={5}
                borderRadius="md"
                focusBorderColor="blue.400"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="medium" color="gray.700">
                메모
              </FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
                borderRadius="md"
                focusBorderColor="blue.400"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="medium" color="gray.700">
                분위기 (다중 선택)
              </FormLabel>
              <Box p={4} bg="blue.50" borderRadius="lg">
                <Box>
                  {MOODS.map((mood) => (
                    <Button
                      key={mood.value}
                      size="sm"
                      variant={
                        formData.mood?.includes(mood.value)
                          ? "solid"
                          : "outline"
                      }
                      colorScheme="blue"
                      mr={2}
                      mb={2}
                      onClick={() => handleMoodToggle(mood)}
                    >
                      {mood.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            </FormControl>
            <Box mb={2} color="gray.600" fontSize="sm">
              파일은 변경할 수 없습니다.
            </Box>
            <Box mb={2} color="gray.500" fontSize="xs">
              (MP3/WAV 파일은 최초 등록 시에만 업로드할 수 있습니다.)
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter p={4} borderTopWidth={1} borderColor="gray.200">
          <Button variant="ghost" mr={3} onClick={onClose}>
            취소
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="저장 중..."
          >
            저장
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TrackEditModal;
